import { useState } from "react";
import type { FormEvent } from "react";
import Seo, { SITE_URL } from "@/components/feature/Seo";

const FORM_URL = "https://readdy.ai/api/form/da21f8dv7gurs51ef3fg";

interface FormStatus {
  kind: "idle" | "success" | "error";
  message: string;
}

export default function Contact() {
  const [status, setStatus] = useState<FormStatus>({ kind: "idle", message: "" });
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    const honeypot = String(fd.get("contact_alt")?.toString() ?? "").trim();
    if (honeypot) {
      setStatus({ kind: "success", message: "感谢你的留言！我们已收到你的消息，会尽快回复你。" });
      form.reset();
      return;
    }

    const name = String(fd.get("name") ?? "");
    const email = String(fd.get("email") ?? "");
    const topic = String(fd.get("topic") ?? "");
    const message = String(fd.get("message") ?? "");
    if (!name.trim() || !email.trim() || !message.trim()) {
      setStatus({ kind: "error", message: "请填写姓名、邮箱和留言内容后再提交。" });
      return;
    }
    if (message.trim().length > 500) {
      setStatus({ kind: "error", message: "留言内容不能超过 500 字，请精简后再次提交。" });
      return;
    }

    setSubmitting(true);
    setStatus({ kind: "idle", message: "" });
    const payload = new FormData(form);
    payload.delete("contact_alt");

    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(payload as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try {
        parsed = responseText ? JSON.parse(responseText) : {};
      } catch {
        parsed = {};
      }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = /spam|form data is spam/i.test(serverMsg);

      if (res.ok && parsed?.code === "OK" && !isSpam) {
        setStatus({ kind: "success", message: "感谢你的留言！我们已收到你的消息，会尽快回复你。" });
        form.reset();
      } else {
        setStatus({
          kind: "error",
          message: serverMsg || "提交失败，请稍后再试。",
        });
      }
    } catch {
      setStatus({ kind: "error", message: "网络异常，提交失败，请稍后再试。" });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <Seo
        title="联系我们 - 天天漫画网"
        description="如有任何关于长期连载漫画的建议、合作或使用问题，欢迎通过天天漫画网联系我们，我们将尽快为你解答。"
        keywords="天天漫画网,联系我们,意见反馈,合作,追更"
        canonical={`${SITE_URL}/contact`}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "ContactPage",
            name: "联系天天漫画网",
            url: `${SITE_URL}/contact`,
            description: "联系天天漫画网，提交你的建议或合作意向。",
          },
        ]}
      />

      <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6 md:py-14">
        <nav aria-label="面包屑" className="flex items-center gap-1.5 text-xs text-foreground-500">
          <a href="/" className="hover:text-primary-600">首页</a>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-700">联系我们</span>
        </nav>

        <header className="mt-5 max-w-2xl">
          <h1 className="font-heading text-2xl text-foreground-950 md:text-3xl">联系我们</h1>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600">
            对长期连载内容有任何建议、反馈，或有合作意向，欢迎通过下方表单与我们联系。
            我们会认真阅读每一封留言，并在第一时间回复你。
          </p>
        </header>

        <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_380px]">
          <div className="rounded-lg border border-background-200/70 bg-background-100 p-6 md:p-8">
            <h2 className="text-lg font-bold text-foreground-950">发送留言</h2>
            <form
              data-readdy-form
              className="mt-6 space-y-5"
              onSubmit={handleSubmit}
            >
              <div className="form-honeypot-field" aria-hidden="true">
                <input
                  type="text"
                  name="contact_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  readOnly
                />
              </div>

              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-foreground-800">
                    你的称呼
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    placeholder="怎么称呼你"
                    className="w-full rounded-md border border-background-200 bg-background-50 px-4 py-2.5 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground-800">
                    联系邮箱
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="you@example.com"
                    className="w-full rounded-md border border-background-200 bg-background-50 px-4 py-2.5 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="topic" className="mb-1.5 block text-sm font-medium text-foreground-800">
                  留言主题
                </label>
                <select
                  id="topic"
                  name="topic"
                  className="w-full rounded-md border border-background-200 bg-background-50 px-4 py-2.5 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                >
                  <option value="内容建议">内容建议</option>
                  <option value="问题反馈">问题反馈</option>
                  <option value="合作洽谈">合作洽谈</option>
                  <option value="其他">其他</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="mb-1.5 block text-sm font-medium text-foreground-800">
                  留言内容
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={5}
                  maxLength={500}
                  placeholder="想对我们说点什么（500 字以内）"
                  className="w-full resize-none rounded-md border border-background-200 bg-background-50 px-4 py-2.5 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="whitespace-nowrap rounded-md bg-primary-500 px-7 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-60"
              >
                {submitting ? "提交中…" : "发送留言"}
              </button>

              {status.kind === "success" && (
                <p className="flex items-center gap-2 rounded-md bg-secondary-100 px-4 py-3 text-sm text-secondary-800">
                  <i className="ri-checkbox-circle-line"></i>{status.message}
                </p>
              )}
              {status.kind === "error" && (
                <p className="flex items-center gap-2 rounded-md bg-primary-100 px-4 py-3 text-sm text-primary-800">
                  <i className="ri-error-warning-line"></i>{status.message}
                </p>
              )}
            </form>
          </div>

          <aside className="flex flex-col gap-5">
            <div className="rounded-lg border border-background-200/70 bg-background-100 p-6">
              <h2 className="text-base font-bold text-foreground-950">其他联系方式</h2>
              <ul className="mt-4 space-y-4 text-sm text-foreground-700">
                <li className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className="ri-mail-line"></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-500">电子邮箱</p>
                    <p className="font-medium">hello@tiantianmh.com</p>
                  </div>
                </li>
                <li className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-accent-100 text-accent-600">
                    <i className="ri-wechat-line"></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-500">微信公众号</p>
                    <p className="font-medium">天天漫画网</p>
                  </div>
                </li>
                <li className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
                    <i className="ri-time-line"></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-500">客服时间</p>
                    <p className="font-medium">每日 9:00 – 22:00</p>
                  </div>
                </li>
              </ul>
            </div>

            <div className="rounded-lg border border-background-200/70 bg-background-100 p-6">
              <h2 className="text-base font-bold text-foreground-950">常见问题</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                关于追更、更新频率与使用方式，你可能想先看看常见问题解答。
              </p>
              <a
                href="/"
                className="mt-4 inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-600 hover:text-primary-700"
              >
                回到首页 <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </aside>
        </div>
      </div>
    </>
  );
}